/**
 * Display name from a Cognito ID token, same order as SCM /auth/me:
 * display_name, name, given_name + family_name, then the email local-part.
 *
 * Initials use the first letter of each word, up to three. A three-part name
 * such as "Gabriel Rosas Guzmán" is GRG; a two-word name stays two letters.
 * Given name + family name are used when they contain at least as many words
 * as the full name; otherwise the longer full name wins so a middle surname
 * is not dropped. Otherwise the email local-part.
 */

export interface NameClaims {
  name?: unknown;
  display_name?: unknown;
  given_name?: unknown;
  family_name?: unknown;
  email?: unknown;
}

function claim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function emailLocalPart(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return '';
  const at = trimmed.indexOf('@');
  return (at >= 0 ? trimmed.slice(0, at) : trimmed).trim();
}

export function displayNameFromClaims(
  claims: NameClaims,
  fallbackEmail = '',
): string {
  const named = claim(claims.display_name) || claim(claims.name);
  if (named) return named;

  const combined = [claim(claims.given_name), claim(claims.family_name)]
    .filter(Boolean)
    .join(' ');
  if (combined) return combined;

  const local = emailLocalPart(claim(claims.email) || fallbackEmail);
  return local || 'Usuario';
}

/** Up to three words → first letter of each; one word → first two chars; else email. */
export function initialsFromDisplayName(name: string, email: string): string {
  const fromWords = initialsFromNameWords(name);
  if (fromWords) return fromWords;
  return initialsFromEmail(email);
}

function nameWords(value: string): string[] {
  return value.trim().split(/\s+/).filter(Boolean);
}

function initialsFromNameWords(name: string): string | null {
  const parts = nameWords(name);
  if (parts.length >= 2) {
    const letters = parts
      .slice(0, 3)
      .map((part) => part[0] || '')
      .join('');
    if (letters.length >= 2) return letters.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return null;
}

function initialsFromEmail(email: string): string {
  const local = emailLocalPart(email).replace(/[^a-zA-Z0-9]/g, '');
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  if (local.length === 1) return (local + local).toUpperCase();
  return 'GG';
}

/**
 * Prefer given_name + family_name when that pair has at least as many words
 * as the display name. A longer full name wins so "Gabriel" + "Guzmán" does
 * not collapse "Gabriel Rosas Guzmán" to GG.
 */
export function initialsFromClaims(
  claims: NameClaims,
  fallbackEmail = '',
): string {
  const combined = [claim(claims.given_name), claim(claims.family_name)]
    .filter(Boolean)
    .join(' ');
  const full = claim(claims.display_name) || claim(claims.name);
  const chosen =
    nameWords(full).length > nameWords(combined).length ? full : combined || full;
  const fromName = initialsFromNameWords(chosen);
  if (fromName) return fromName;
  return initialsFromEmail(claim(claims.email) || fallbackEmail);
}

const GROUP_PRIORITY = [
  'Admins',
  'Marketing',
  'BackupsBO',
  'BackupsPE',
  'BackupsPY',
] as const;

/** Role pill: Admins wins when present, otherwise the first known Cognito group. */
export function primaryCognitoGroup(groups: readonly string[]): string {
  for (const group of GROUP_PRIORITY) {
    if (groups.includes(group)) return group;
  }
  return groups.find((group) => group.trim()) || '';
}
